/**
 * The ticket bought (docs/NEW-UI/07 D-01..D-03). Three looks of the same props:
 *  - standard        the generic ticket's (platform Ticket), with the lines it has always had;
 *  - card            the generic ticket's card (Essenziale), and under its subtitle the chip of the changes, the
 *                    itinerary as a line down the stops over the card, as on the theme's train (TrainSingle);
 *  - classic         the old kiosk's (backend/frontend/src/components/touch/top-bar/PurchasedTicket.tsx): the times
 *                    across the top, the travellers with their coach and seat and the QR on the left, the train,
 *                    class, price, coach, seat, PNR and CP on the right, and the bar of its changes at the foot: it
 *                    opens the itinerary over the ticket, by touch or voice (TrainItinerary.tsx).
 */
import { QRCodeSVG } from 'qrcode.react';
import { z } from 'zod';
import { formatMoney } from '@platform/i18n/translator';
import { Icon } from '@platform/ui-kit/Icon';
import { ticket, TicketCardView } from '@platform/widgets/generic/Ticket';
import { defineWidget, variantsOf, type WidgetDefinition, type WidgetProps } from '@platform/widgets/contract';
import { journeyItinerary } from './itinerary';
import { ChangesButton, ItineraryCover } from './TrainItinerary';
import { classOnly, extraChips, journeySchema } from './trip';
import css from './TripCards.module.css';

type TicketView = typeof ticket extends WidgetDefinition<infer P> ? P : never;

const schema = z.object({
  /** What the generic looks show: the generic ticket's props, as the builder has always made them. */
  view: ticket.schema as z.ZodType<TicketView>,
  trip: journeySchema.extend({
    total: z.number().nullable(),
    coach: z.string().nullable(),
    platform: z.string().nullable(),
    pnr: z.string().nullable(),
    cp: z.string().nullable(),
    qr: z.string().optional(),
  }),
});

type Props = z.infer<typeof schema>;
type T = WidgetProps<Props>['ctx']['t'];

/** A colour per traveller, the same on their coach and seat chips. */
const TRAVELLER_TONES = ['accent', 'green', 'violet', 'amber'] as const;

/** «Adulto 1», «Adulto 2», «Bambino»: each with the seat in the same order as the booking's. */
function travellers(trip: Props['trip'], t: T) {
  const named = (word: string, n: number, i: number) => (n > 1 ? `${t(word)} ${i + 1}` : t(word));
  const labels = [
    ...Array.from({ length: trip.adults }, (_, i) => named('trains.done.adult', trip.adults, i)),
    ...Array.from({ length: trip.children }, (_, i) => named('trains.done.child', trip.children, i)),
  ];
  return labels.map((label, i) => ({ label, coach: trip.seats[i]?.coach ?? trip.coach ?? '', seat: trip.seats[i]?.seat ?? '' }));
}

function travellersTotal(trip: Props['trip'], t: T): string {
  const n = trip.adults + trip.children;
  if (n === 1) return t('trains.done.pax_one');
  return trip.children > 0
    ? t('trains.done.pax_many_children', { n, adults: trip.adults, children: trip.children })
    : t('trains.done.pax_many', { n, adults: trip.adults });
}

/** The PNR with its second-last letter large, as the old kiosk printed it (the one people misread). */
function Pnr({ value }: { value: string }) {
  if (value.length < 2) return <>{value}</>;
  return (
    <>
      <span className={css.pnrSoft}>{value.slice(0, -2)}</span>
      <span className={css.pnrMark}>{value.slice(-2, -1)}</span>
      <span className={css.pnrSoft}>{value.slice(-1)}</span>
    </>
  );
}

function ClassicTicket({ props, ctx, api }: WidgetProps<Props>) {
  const { t, language } = ctx;
  const { trip } = props;
  const itinerary = journeyItinerary(trip);
  const people = travellers(trip, t);
  const seats = trip.seats.map((s) => s.seat).join(', ');
  return (
    <section className={css.card} aria-label={t('trains.done.classic_title')}>
      <header className={css.header}><h2>🎫 {t('trains.done.classic_title')}</h2></header>
      <div className={css.timesChip}>
        <span>{trip.origin}</span>
        <strong className={css.departure}>{trip.departure}</strong>
        <Icon name="next" size={28} />
        <strong>{trip.arrival}</strong>
        <span>{trip.destination}</span>
      </div>
      <div className={css.columns}>
        <div className={css.left}>
          <div className={css.people}>
            <p className={css.peopleTitle}>{t('trains.done.total_pax', { pax: travellersTotal(trip, t) })}</p>
            {people.map((p, i) => (
              <p key={p.label} className={css.person} data-tone={TRAVELLER_TONES[i % TRAVELLER_TONES.length]}>
                <span>{p.label}</span>
                <span className={css.personChips}>
                  {p.coach && <span className={css.personChip}><Icon name="coach" size={20} />{p.coach}</span>}
                  {p.seat && <span className={css.personChip}><Icon name="seat" size={20} />{p.seat}</span>}
                </span>
              </p>
            ))}
          </div>
          {extraChips(trip.extras, t).length > 0 && (
            <div className={css.chips}>
              {extraChips(trip.extras, t).map((x) => <span key={x.key} className={css.chip}><Icon name={x.icon} size={22} />{x.label}</span>)}
            </div>
          )}
          {trip.qr && (
            <div className={css.qrBlock}>
              <p>{t('trains.done.qr_take')}</p>
              <QRCodeSVG value={trip.qr} size={260} level="M" marginSize={1} bgColor="transparent" fgColor="currentColor"
                title={t('trains.done.qr')} role="img" aria-label={t('trains.done.qr')} />
            </div>
          )}
        </div>
        <div className={`${css.right} ${itinerary && itinerary.changes > 0 ? css.withChanges : ''}`}>
          <div className={css.facts}>
            <p className={css.fact}><Icon name="screen" size={30} className={css.factTypeIcon} /><span><strong className={css.trainType}>{trip.trainType}</strong><small>{t('trains.done.train_type')}</small></span></p>
            <p className={css.fact}><Icon name="train" size={30} /><span><strong>{trip.trainNumber}</strong><small>{t('trains.done.train_number')}</small></span></p>
            <p className={css.fact}><Icon name="class" size={30} /><span><strong>{trip.travelClass ? classOnly(trip.travelClass) : '—'}</strong><small>{t('trains.summary.class')}</small></span></p>
            <p className={css.fact}><Icon name="price" size={30} /><span><strong>{trip.total === null ? '—' : formatMoney(trip.total, language)}</strong><small>{t('trains.done.price')}</small></span></p>
          </div>
          <div className={css.tiles}>
            <p className={css.tile}><Icon name="coach" size={44} /><strong>{trip.coach || '—'}</strong></p>
            {trip.platform
              ? <p className={`${css.tile} ${css.tileQuiet}`}><Icon name="info" size={40} /><strong>{trip.platform}</strong></p>
              : <p className={css.tile}><Icon name="seat" size={44} /><strong>{seats || '—'}</strong></p>}
          </div>
          <div className={css.codes}>
            <p className={css.codeBox}><small>{t('trains.done.pnr_short')}</small><span className={css.pnr}>{trip.pnr ? <Pnr value={trip.pnr} /> : '—'}</span></p>
            <p className={css.codeBox}><small>{t('trains.done.cp_short')}</small><span className={css.cp}>{trip.cp ?? '—'}</span></p>
          </div>
          {/* the old kiosk's bar across the foot, the itinerary over the ticket (TrainItinerary.tsx) */}
          {itinerary && itinerary.changes > 0 && <ChangesButton of={itinerary} api={api} t={t} look="bar" className={css.changesBar} />}
        </div>
      </div>
      {itinerary && <ItineraryCover of={itinerary} t={t} closeLabel={t('overlay.close')} look="cards" headerClassName={css.coverHead} />}
    </section>
  );
}

// the generic ticket's other look, the one the Essenziale theme prefers (`card`)
const genericCard = variantsOf(ticket).find((v) => v.id === 'card')!;

function CardTicket({ props, ctx, api }: WidgetProps<Props>) {
  const { t } = ctx;
  const itinerary = journeyItinerary(props.trip);
  return (
    <TicketCardView props={props.view}>
      {itinerary && itinerary.changes > 0 && <ChangesButton of={itinerary} api={api} t={t} look="chip" className={css.cardChanges} />}
      {itinerary && <ItineraryCover of={itinerary} t={t} closeLabel={t('overlay.close')} look="timeline" />}
    </TicketCardView>
  );
}

export const tripTicket = defineWidget({
  key: 'trip-ticket',
  schema,
  variants: [
    { id: genericCard.id, label: genericCard.label, Component: CardTicket },
    { id: 'classic', label: 'widget.variant.classic', Component: ClassicTicket },
  ],
  Component: ({ props, ctx, api }) => <ticket.Component props={props.view} ctx={ctx} api={api} />,
});
